// Pure, deterministic dungeon-floor generation. No Pixi, no DOM. (TECHNICAL_DESIGN.md §10, §14.)
// genFloor(seed, floor, nonce) is pure; game.ts supplies a runtime visit nonce for freshness.
import { Rng, hash3i } from './rng';

export const FLOOR_SIZE = 48;
export const DUNGEON_MAX_FLOOR = 10; // dungeons are a finite 10-floor run; tiers are the replay axis

export type BossKind = 'mini' | 'big' | 'final';
/** Boss cadence within the 10-floor run: 3 & 8 mini, 5 big, 10 the final Dungeon Boss. */
export function bossKindForFloor(floor: number): BossKind | null {
  if (floor === 10) return 'final';
  if (floor === 5) return 'big';
  if (floor === 3 || floor === 8) return 'mini';
  return null;
}

/** The boss's display name for a tier — single source of truth for the HUD and the spawn label. */
export function bossName(kind: BossKind): string {
  return kind === 'final' ? 'The Hollow King' : kind === 'big' ? 'Bone Warden' : 'Warden Acolyte';
}

export interface Rect { x: number; y: number; w: number; h: number }
export interface Pack { tx: number; ty: number; defId: string; count: number }
export interface DungeonFloor {
  size: number;
  tiles: Uint8Array; // size*size: 0 = wall, 1 = floor
  rooms: Rect[];
  entry: { tx: number; ty: number };
  stairs: { tx: number; ty: number };
  packs: Pack[];
  chests: { tx: number; ty: number }[];
  traps: { tx: number; ty: number }[]; // visible spike tiles — walkable but they bite
  boss: { tx: number; ty: number } | null;
}

/** Per-floor stat multiplier: +35% per floor (design contract). */
export function mobStatScale(floor: number): number {
  return 1 + 0.35 * floor;
}
/** Loot value scales with the same curve. */
export function floorLootBudget(floor: number): number {
  return Math.round(10 * mobStatScale(floor));
}

const center = (r: Rect) => ({ x: Math.floor(r.x + r.w / 2), y: Math.floor(r.y + r.h / 2) });

function carveH(t: Uint8Array, n: number, x0: number, x1: number, y: number): void {
  for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++) t[y * n + x] = 1;
}
function carveV(t: Uint8Array, n: number, y0: number, y1: number, x: number): void {
  for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++) t[y * n + x] = 1;
}

export function isWall(f: DungeonFloor, tx: number, ty: number): boolean {
  if (tx < 0 || ty < 0 || tx >= f.size || ty >= f.size) return true; // out of bounds = solid
  return f.tiles[ty * f.size + tx] === 0;
}

export function genFloor(seed: number, floor: number, nonce: number): DungeonFloor {
  const n = FLOOR_SIZE;
  const rng = new Rng(hash3i(seed, floor, nonce, 0x44));
  const tiles = new Uint8Array(n * n); // all walls
  const rooms: Rect[] = [];
  const target = 6 + rng.int(3); // 6-8 rooms

  for (let tries = 0; tries < target * 5 && rooms.length < target; tries++) {
    const w = 5 + rng.int(6), h = 5 + rng.int(6);
    const x = 1 + rng.int(n - w - 2), y = 1 + rng.int(n - h - 2);
    if (rooms.some((r) => x < r.x + r.w + 1 && x + w + 1 > r.x && y < r.y + r.h + 1 && y + h + 1 > r.y)) continue;
    rooms.push({ x, y, w, h });
    for (let ty = y; ty < y + h; ty++) for (let tx = x; tx < x + w; tx++) tiles[ty * n + tx] = 1;
  }

  // connect each room to the previous (guarantees full connectivity)
  for (let i = 1; i < rooms.length; i++) {
    const a = center(rooms[i - 1]), b = center(rooms[i]);
    carveH(tiles, n, a.x, b.x, a.y);
    carveV(tiles, n, a.y, b.y, b.x);
  }

  const entry = { tx: center(rooms[0]).x, ty: center(rooms[0]).y };
  const last = center(rooms[rooms.length - 1]);
  const stairs = { tx: last.x, ty: last.y };

  const packs: Pack[] = [];
  const packN = 2 + rng.int(3); // 2-4
  for (let i = 0; i < packN; i++) {
    const r = rooms[1 + rng.int(Math.max(1, rooms.length - 1))];
    const c = center(r);
    packs.push({ tx: c.x, ty: c.y, defId: 'skeleton', count: 2 + rng.int(2) });
  }

  const chests: { tx: number; ty: number }[] = [];
  const chestN = 2 + rng.int(2); // 2-3
  for (let i = 0; i < chestN; i++) {
    const r = rooms[rng.int(rooms.length)];
    chests.push({ tx: r.x + 1 + rng.int(Math.max(1, r.w - 2)), ty: r.y + 1 + rng.int(Math.max(1, r.h - 2)) });
  }

  // spike traps: visible, walkable hazards you route around — more of them the deeper you go.
  const traps: { tx: number; ty: number }[] = [];
  const trapN = Math.min(16, 3 + floor * 2);
  const nearSafe = (tx: number, ty: number) =>
    (Math.abs(tx - entry.tx) <= 2 && Math.abs(ty - entry.ty) <= 2)
    || (Math.abs(tx - stairs.tx) <= 1 && Math.abs(ty - stairs.ty) <= 1);
  for (let i = 0; i < trapN * 6 && traps.length < trapN; i++) {
    const tx = 1 + rng.int(n - 2), ty = 1 + rng.int(n - 2);
    if (tiles[ty * n + tx] !== 1) continue;      // floor only
    if (nearSafe(tx, ty)) continue;               // never trap the entrance/stairs
    if (chests.some((c) => c.tx === tx && c.ty === ty)) continue;
    if (traps.some((t) => t.tx === tx && t.ty === ty)) continue;
    traps.push({ tx, ty });
  }

  const boss = bossKindForFloor(floor) ? { tx: stairs.tx, ty: stairs.ty } : null;
  return { size: n, tiles, rooms, entry, stairs, packs, chests, traps, boss };
}

/** Flood-fill reachability from entry — used by tests to prove stairs are always reachable. */
export function reachable(f: DungeonFloor, from: { tx: number; ty: number }, to: { tx: number; ty: number }): boolean {
  const seen = new Uint8Array(f.size * f.size);
  const stack = [from.ty * f.size + from.tx];
  while (stack.length) {
    const idx = stack.pop()!;
    if (seen[idx]) continue;
    seen[idx] = 1;
    const tx = idx % f.size, ty = Math.floor(idx / f.size);
    if (tx === to.tx && ty === to.ty) return true;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = tx + dx, ny = ty + dy;
      if (!isWall(f, nx, ny) && !seen[ny * f.size + nx]) stack.push(ny * f.size + nx);
    }
  }
  return false;
}
